import './productDetails.scss';
import { FC, useCallback, useState } from 'react';
import {
  Button,
  Checkbox,
  ClickableImage,
  DropdownSelect,
  LoaderIndicator,
  Message,
  OutlinedInput as Input,
  Tag,
} from 'tedooo-web-design-system/ui';
import classNames from 'classnames';
import {
  AlertTriangle,
  Checkmark,
  ExternalLinkIcon,
  PaypalLogo,
  PlusIcon,
} from 'tedooo-web-design-system/icons';

import ShopOwnerButtons from './ShopOwnerButtons';
import { isEmptyArray, toCurrency, toMonthAndDaysFormat } from 'utils';
import { IProductDetails, NonSignInPopupSources } from 'types';
import { translations } from 'translations/en';
import FileUploader from 'components/fileUploader/FileUploader';
import { Image } from 'components/helpers';
import { getQuantityDropdownOptions, getQuantityText } from '../helper';
import ProductBanner from '../productBanner/ProductBanner';
import { Countries } from 'constant/countries';
import { useRequireToken } from 'hooks/useRequireToken';

const { CHANGE_COUNTRY, NOT_SHIPPING_TO_COUNTRY } = translations.SPECIFIC_PRODUCT.PRODUCT_DETAILS;

const ProductDetails: FC<IProductDetails> = ({
  title,
  price,
  originalPrice,
  bannerDetails,
  currencyCode,
  selectedQuantity,
  vacationDetails,
  shouldShowPaypalButton,
  labels,
  isDigital,
  isItemInBasket,
  isShopOwner,
  quantity,
  shippingCountries,
  recipientCountry,
  shippingInformation,
  estimatedDeliveryTime,
  onChangeQuantity,
  onAddToBasket,
  onBuyNow,
  onChangeCountry,
  onEditListing,
  onShareListing,
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const [isImageLoading, setIsImageLoading] = useState(false);

  const [addedImages, setAddedImages] = useState<string[]>([]);

  const [customDescription, setCustomDescription] = useState('');

  const discountPercentage = originalPrice !== price ? 100 - (price * 100) / originalPrice : 0;

  const quantityOptions = getQuantityDropdownOptions(quantity);

  const isShippingToCountry = useCallback(() => {
    const isShippingToWorldWide = shippingCountries.includes(Countries.WorldWide);

    return (
      isShippingToWorldWide || (recipientCountry && shippingCountries.includes(recipientCountry))
    );
  }, [shippingCountries, recipientCountry, isDigital, isShopOwner]);

  const shouldShowCountryError =
    !isShopOwner && !!recipientCountry && !isDigital && !isShippingToCountry();

  const isButtonDisabled = shouldShowCountryError || !!vacationDetails || quantity === 0;

  const minDeliveryDate = estimatedDeliveryTime
    ? toMonthAndDaysFormat(estimatedDeliveryTime.min)
    : null;

  const maxDeliveryDate = estimatedDeliveryTime
    ? toMonthAndDaysFormat(estimatedDeliveryTime.max)
    : null;

  const onQuantityChange = (value: string) => {
    onChangeQuantity(Number(value));
  };

  const onCheckboxChange = () => {
    const newValue = !isChecked;
    setIsChecked(newValue);
    if (!newValue) {
      setCustomDescription('');
      setAddedImages([]);
    }
  };

  const onChangeInput = (e: any) => {
    setCustomDescription(e.target.value);
  };

  const onImageUpload = (newImages: string[]) => {
    setIsImageLoading(false);
    setAddedImages([...newImages, ...addedImages]);
  };

  const onAddToBasketClick = useRequireToken(() => {
    onAddToBasket({ text: customDescription, images: addedImages });
  }, NonSignInPopupSources.AddToBasket);

  const onBuyNowClick = () => {
    onBuyNow({ text: customDescription, images: addedImages }, false);
  };

  const onBuyWithPaypalClick = () => {
    onBuyNow({ text: customDescription, images: addedImages }, true);
  };

  const onImageLoad = () => {
    setIsImageLoading(true);
  };

  const shouldShowEstimatedDeliveryTime = minDeliveryDate && maxDeliveryDate && !isShopOwner;

  const shouldDisplayShippingInformationError = !shippingInformation && isShopOwner && !isDigital;

  const shouldShowTagContainer = !isEmptyArray(labels) || shouldShowEstimatedDeliveryTime;

  const basketButtonText = isItemInBasket
    ? translations.SHOP.ADDED_TO_BASKET
    : translations.SHOP.ADD_TO_BASKET;

  const basketButtonIcon = isItemInBasket ? <Checkmark /> : undefined;

  const isInStockTag = !!quantity && quantity > 3;

  return (
    <div className="product-details-container">
      <div className="top-section">
        <span className={classNames('quantity-label', { 'in-stock': isInStockTag })}>
          {getQuantityText(quantity)}
        </span>
        <div className="price-container">
          <span className="price">{toCurrency(price, currencyCode, 2)}</span>
          {originalPrice && originalPrice !== price && (
            <>
              <span className="original-price">{toCurrency(originalPrice, currencyCode)}</span>
              <span className="discount">{`(${Math.round(discountPercentage)}% off)`}</span>
            </>
          )}
        </div>
        <span className="title">{title}</span>
        {shouldShowTagContainer && (
          <div className="tag-container">
            {labels.map((label, i) => (
              <Tag
                color={{
                  backgroundColor: label.backgroundColor,
                  textColor: label.textColor,
                }}
                size="small"
                text={label.text}
                key={i}
              />
            ))}
            {shouldShowEstimatedDeliveryTime && (
              <div className="estimated-delivery-time-tag-container">
                <span className="tag-text">{`Est. arrival ${minDeliveryDate} - ${maxDeliveryDate}`}</span>
              </div>
            )}
          </div>
        )}
        {bannerDetails && !isShopOwner && <ProductBanner details={bannerDetails} />}
        {shouldShowCountryError && (
          <Message
            message={
              <>
                {NOT_SHIPPING_TO_COUNTRY} {recipientCountry}
                <span onClick={onChangeCountry} className="underline">
                  {CHANGE_COUNTRY}
                </span>
              </>
            }
            type="warning"
            prefixIcon={<AlertTriangle />}
          />
        )}
        {shouldDisplayShippingInformationError && (
          <Button
            type="danger"
            size="small"
            borderRadius="small"
            text="Update shipping info"
            suffixIcon={<ExternalLinkIcon />}
            onClick={onEditListing}
          />
        )}
        {isShopOwner && (
          <ShopOwnerButtons onEditItemClick={onEditListing} onShareClick={onShareListing} />
        )}
      </div>
      {!isShopOwner && (
        <div className="bottom-section">
          <div className="customization-container">
            {quantity !== 0 && (
              <DropdownSelect
                size="medium"
                options={quantityOptions}
                onChange={onQuantityChange}
                value={String(selectedQuantity)}
                label="Quantity"
              />
            )}
            <span className="sub-title">{translations.SPECIFIC_PRODUCT.NOTE_TO_SELLER}</span>
            <Checkbox
              text={translations.SPECIFIC_PRODUCT.NOTE_CHECKBOX_TEXT}
              checked={isChecked}
              onChange={onCheckboxChange}
            />
            {isChecked && (
              <>
                <Input
                  placeholder={translations.COMMON.DESCRIPTION}
                  id="description"
                  value={customDescription}
                  onChange={onChangeInput}
                />
                <div className="images-container">
                  <FileUploader
                    maxItems={4}
                    id="custom-images"
                    onImageUpload={onImageUpload}
                    onImagesLoadingStart={onImageLoad}
                  >
                    <ClickableImage icon={<PlusIcon />} />
                  </FileUploader>
                  {isImageLoading && (
                    <div className="loader-container">
                      <LoaderIndicator size={30} />
                    </div>
                  )}
                  {addedImages.map((image, i) => (
                    <Image src={image} className="added-image" key={i} />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="buttons-container">
            <Button
              type="primary"
              size="medium"
              borderRadius="large"
              text={translations.SHOP.BUY_THIS_ITEM}
              disabled={isButtonDisabled}
              isFullWidth
              onClick={onBuyNowClick}
            />
            {!isDigital && (
              <Button
                type="secondary"
                size="medium"
                borderRadius="large"
                isFullWidth
                disabled={isButtonDisabled}
                text={basketButtonText}
                prefixIcon={basketButtonIcon}
                onClick={onAddToBasketClick}
              />
            )}
            {shouldShowPaypalButton && (
              <Button
                type="primary"
                borderRadius="large"
                size="medium"
                isFullWidth
                className="paypal-button"
                text={translations.SHOP.BUY_NOW_WITH}
                onClick={onBuyWithPaypalClick}
                suffixIcon={<PaypalLogo />}
                disabled={isButtonDisabled}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
